import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { ToastService } from '../../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../../shared/services/error-service';

import { SalesBatchResult, SalesService, SalesWorksheetSelectionItem, SalesWorksheetInput, ClientService, ClientAreaService, WorkbookService } from '../../../../../../service/proxy';

@Component({
    selector: 'sales-sheet-selection',
    templateUrl: './sales-sheet-selection.component.html',
    styleUrls: ['./sales-sheet-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        SalesService,
        WorkbookService,
        ClientService,
        ClientAreaService,
        WorkbookService,
        ToastService,
        ErrorService
    ]
})
export class SalesSheetSelectionComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    batchId: number = 0;
    isWorksheetSelected: boolean = false;
    loading: boolean = false;

    public clients: { id: number; name: string; }[] = [];
    public selectedClientId: number = 0;

    public clientAreas: { id: number; name: string; }[] = [];
    public selectedClientAreaId: number = 0;

    public batch: SalesBatchResult = new SalesBatchResult();
    public worksheets: any[] = [];
    public worksheetSelection: SalesWorksheetSelectionItem[] = [];

    constructor(
        private route: ActivatedRoute,
        private salesService: SalesService,
        private clientAreaService: ClientAreaService,
        private workbookService: WorkbookService,
        private toastService: ToastService,
        private errorService: ErrorService,
    ) {}

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => {
            this.selectedClientId = Number(params.get('clientId'));
            this.getClientAreas();
        });
    }

    getBatch() {
        this.salesService.getSalesBatch(this.batchId)
            .subscribe((result) => {
                this.batch = result;

                if (!this.selectedClientId && result.clientId) {
                    this.selectedClientId = result.clientId;
                }

                this.getClientAreas();
                this.getWorksheetSelection();
            }
        );
    }

    getWorksheetSelection(): void {
        this.salesService.getSalesWorksheetSelection(this.batchId)
            .subscribe({
                next: (data: any) => {
                    if (data && data.results && data.results.length > 0) {
                        this.worksheetSelection = data.results.map((item: any) => {
                            const selection = new SalesWorksheetSelectionItem();

                            selection.worksheet = item.worksheetName;
                            selection.selected = item.isSelected;
                            selection.clientAreaId = item.clientAreaId;

                            return selection;
                        });
                    }
                    else {
                        this.getWorksheets();
                    }
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            });
    }

    getClientAreas(): void {
        if (!this.selectedClientId) {
            return;
        }

        this.clientAreaService.getClientAreaLookup(this.selectedClientId)
            .subscribe({
                next: (clientAreas) => {
                    if (!clientAreas.length) {
                        this.toastService.show('No policies found for the selected client', 'info');
                        return;
                    }

                    this.clientAreas = clientAreas;
                },
                error: (error) => {
                    this.errorService.handleError(error);
                }
            });
    }

    getWorksheets(): void {
        this.loading = true;
        this.workbookService.getSheetNames(`${this.batch.filePath}\\${this.batch.storedName}`)
            .subscribe({
                next: (data) => {
                    this.worksheets = data.results || [];

                    if (!this.worksheetSelection || this.worksheetSelection['length'] < 1) {
                        this.worksheets.forEach(worksheet => {
                            const worksheetSelectionItem = new SalesWorksheetSelectionItem();

                            worksheetSelectionItem.worksheet = worksheet;
                            worksheetSelectionItem.selected = false;
                            worksheetSelectionItem.clientAreaId = undefined;

                            this.worksheetSelection['push'](worksheetSelectionItem);
                        });
                    }
                },
                error: (error) => {
                    if (error.status === 404) {
                        const errorBody = error.response ? JSON.parse(error.response) : null;
                        if (errorBody?.code === 'FILE_NOT_FOUND') {
                            this.toastService.show('Excel file not found. Delete this import batch and upload again.', 'warning');
                        } else {
                            this.toastService.show('File not found. Please try again.', 'info');
                        }
                    } else {
                        this.errorService.handleError(error);
                    }
                    this.loading = false;
                },
                complete: () => {
                    this.loading = false;
                }
            });
    }

    isValid(): boolean {
        this.isWorksheetSelected = this.worksheetSelection.some((selection: SalesWorksheetSelectionItem) => selection.selected);

        if (!this.isWorksheetSelected) {
            return false;
        }

        if (this.form.invalid) {
            return false;
        }

        return true;
    }

    onActivate(): void {
        this.route.paramMap
            .subscribe(params => {
                this.batchId = Number(params.get('id'));

                if (this.batchId > 0) {
                    this.getBatch();
                }
            }
        );
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            const worksheetsToSave = this.worksheetSelection.map(selection => {
                return new SalesWorksheetInput({
                    clientAreaId: selection.clientAreaId,
                    importRangeMappingId: undefined,
                    worksheetName: selection.worksheet,
                    isSelected: selection.selected,
                });
            });

            let saveCount = 0;

            worksheetsToSave.forEach(worksheetInput => {
                this.salesService.createSalesWorksheet(this.batchId, worksheetInput)
                    .subscribe({
                        next: () => {
                            saveCount++;

                            if (saveCount === worksheetsToSave.length) {
                                this.toastService.show('Sales worksheets saved successfully', 'Success');

                                observer.next(true);
                                observer.complete();
                            }
                        },
                        error: (error: any) => {
                            this.errorService.handleError(error);

                            observer.next(false);
                            observer.complete();
                        }
                    }
                );
            });
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}